import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { getAvailableBedrockModels } from '../../services/api/approvedBedrockModelApi';

const BedrockModelBrowser = ({ open, onSelectModel, onClose }) => {
  const [models, setModels] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [modalityFilter, setModalityFilter] = useState('');

  useEffect(() => {
    if (open) {
      fetchModels();
    }
  }, [open]);

  useEffect(() => {
    applyFilters();
  }, [models, searchTerm, providerFilter, modalityFilter]);

  const fetchModels = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAvailableBedrockModels();
      setModels(response.data);
    } catch (err) {
      setError('Failed to load available Bedrock models. Please ensure you have proper AWS credentials.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...models];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        m => m.modelName.toLowerCase().includes(search) ||
             m.providerName.toLowerCase().includes(search) ||
             m.modelId.toLowerCase().includes(search)
      );
    }

    // Provider filter
    if (providerFilter) {
      filtered = filtered.filter(m => m.providerName === providerFilter);
    }

    // Modality filter
    if (modalityFilter) {
      filtered = filtered.filter(m => m.inputModalities.includes(modalityFilter));
    }

    setFilteredModels(filtered);
  };

  const handleSelect = (model) => {
    onSelectModel(model);
    onClose();
  };

  const getUniqueProviders = () => {
    return [...new Set(models.map(m => m.providerName))].sort();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Browse Available Bedrock Models</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, mt: 2 }}>
              <TextField
                placeholder="Search by name, provider, or model ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Provider</InputLabel>
                <Select
                  value={providerFilter}
                  label="Provider"
                  onChange={(e) => setProviderFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {getUniqueProviders().map(provider => (
                    <MenuItem key={provider} value={provider}>{provider}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Modality</InputLabel>
                <Select
                  value={modalityFilter}
                  label="Modality"
                  onChange={(e) => setModalityFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="TEXT">Text</MenuItem>
                  <MenuItem value="IMAGE">Image</MenuItem>
                  <MenuItem value="EMBEDDING">Embedding</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Models Table */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Provider</TableCell>
                    <TableCell>Model Name</TableCell>
                    <TableCell>Model ID</TableCell>
                    <TableCell>Input</TableCell>
                    <TableCell>Output</TableCell>
                    <TableCell>Streaming</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredModels.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No models found matching your filters
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredModels.map((model) => (
                      <TableRow key={model.modelId} hover>
                        <TableCell>{model.providerName}</TableCell>
                        <TableCell>{model.modelName}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                          {model.modelId}
                        </TableCell>
                        <TableCell>
                          {model.inputModalities.map(m => (
                            <Chip key={m} label={m} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                          ))}
                        </TableCell>
                        <TableCell>
                          {model.outputModalities.map(m => (
                            <Chip key={m} label={m} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                          ))}
                        </TableCell>
                        <TableCell>
                          {model.responseStreamingSupported ? 'Yes' : 'No'}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleSelect(model)}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

BedrockModelBrowser.propTypes = {
  open: PropTypes.bool.isRequired,
  onSelectModel: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

export default BedrockModelBrowser;
